import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { withTheme } from "@emotion/react";
import Chart from "react-chartjs-2";

import {
  Card as MuiCard,
  CardContent,
  CardHeader,
  Button as MuiButton,
  CircularProgress,
  Box,
} from "@mui/material";
import { Loop as LoopIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";
import { alpha } from "@mui/material/styles";
import { getDashBoardChartData } from "src/services/generalService";

const Card = styled(MuiCard)(spacing);

const SmallButton = styled(MuiButton)`
  padding: 4px;
  min-width: 0;

  svg {
    width: 0.9em;
    height: 0.9em;
  }
`;

const ChartWrapper = styled.div`
  height: 378px;
`;

const LoaderWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 378px;
`;

/**
 * Reusable LineChart component that fetches data from an API
 * 
 * Usage Example:
 * <LineChartCard
 *   title="Total Revenue"
 *   apiUrl="/api/Dashboard/revenue"
 *   refreshInterval={120000}
 *   mb={6}
 * />
 * 
 * @param {Object} props - Component props
 * @param {string} props.apiUrl - API endpoint to fetch data from
 * @param {Object} props.theme - Theme object from withTheme HOC
 * @param {boolean} props.loading - External loading state (optional)
 * @param {number} props.mb - Margin bottom from MUI spacing (default: 6)
 */
function LineChartCard({
  apiUrl,
  theme,
  loading: externalLoading = false,
  mb = 6,
}) {
  const [chartData, setChartData] = useState(null);
  const [chartTitle, setChartTitle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(300000); // Default to 5 minutes
  

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getDashBoardChartData();

      // Check if response is successful
      if (response?.status !== 200 || !response?.data) {
        throw new Error(
          response?.message || `API Error: ${response?.status || "Unknown"}`
        );
      }

      const { data } = response;

      // Format data for line chart
      const formattedChartData = {
        labels: data.labels || [],
        datasets: (data.datasets || []).map((ds) => ({
          label: ds.label,
          fill: ds.fill !== false,
          borderColor: ds.borderColor || theme.palette.secondary.main,
        //   backgroundColor: ds.backgroundColor || "transparent",
          backgroundColor: ds.backgroundColor ,
          borderDash: ds.borderDash || [],
          tension: ds.tension || 0.4,
          data: ds.data || [],
        })),
      };

      setChartTitle(data.title || "Line Chart");
      setChartData(formattedChartData);
      setRefreshInterval(data.refreshInterval || refreshInterval);
    } catch (err) {
      console.error("Error fetching chart data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  useEffect(() => {
    if (apiUrl) {
      fetchData();

      // Set up refresh interval if specified
      if (refreshInterval) {
        const interval = setInterval(fetchData, refreshInterval);
        return () => clearInterval(interval);
      }
    }
  }, [apiUrl, refreshInterval, theme]);

  const defaultOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
      },
    },
    scales: {
      x: {
        grid: {
          color: "rgba(0,0,0,0.0)",
        },
      },
      y: {
        grid: {
          color: "rgba(0,0,0,0.0375)",
          fontColor: "#fff",
        },
      },
    },
  };

  const isLoading = loading || externalLoading;

  return (
    <Card mb={mb}>
      <CardHeader
        action={
          <SmallButton
            size="small"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <LoopIcon />
          </SmallButton>
        }
        title={chartTitle }
      />
      <CardContent>
        {isLoading ? (
          <LoaderWrapper>
            <CircularProgress />
          </LoaderWrapper>
        ) : error ? (
          <LoaderWrapper>
            <div>Error loading chart: {error}</div>
          </LoaderWrapper>
        ) : chartData ? (
          <ChartWrapper>
            <Chart
              type="line"
              data={chartData}
              options={defaultOptions}
            />
          </ChartWrapper>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default withTheme(LineChartCard);
